import type { QuizCaseDescriptor } from './types'

export interface QuizCaseFilters {
  declaredType?: string
  family?: QuizCaseDescriptor['family']
  mode?: QuizCaseDescriptor['mode']
  variant?: QuizCaseDescriptor['variant']
  renderStatus?: QuizCaseDescriptor['renderStatus']
  answerState?: QuizCaseDescriptor['answerState']
  resultState?: QuizCaseDescriptor['resultState']
  tag?: string
}

export function validateQuizCases(cases: readonly QuizCaseDescriptor[]): readonly QuizCaseDescriptor[] {
  const ids = new Set<string>()

  for (const quizCase of cases) {
    if (ids.has(quizCase.id)) {
      throw new Error(`Duplicate quiz case id: ${quizCase.id}`)
    }
    ids.add(quizCase.id)

    if (quizCase.apiQuizId !== null && typeof quizCase.apiQuizId !== 'string') {
      throw new Error(`apiQuizId must be a string or null: ${quizCase.id}`)
    }

    if (quizCase.renderStatus === 'renderable' && !quizCase.apiQuizId) {
      throw new Error(`Renderable quiz case requires apiQuizId: ${quizCase.id}`)
    }

    if (!quizCase.mode) {
      throw new Error(`Quiz case requires explicit mode: ${quizCase.id}`)
    }

    if (!quizCase.variant) {
      throw new Error(`Quiz case requires explicit variant: ${quizCase.id}`)
    }
  }

  return cases
}

export function groupCasesByType(
  cases: readonly QuizCaseDescriptor[],
): ReadonlyMap<string, readonly QuizCaseDescriptor[]> {
  const groups = new Map<string, QuizCaseDescriptor[]>()

  for (const quizCase of cases) {
    const group = groups.get(quizCase.declaredType) ?? []
    group.push(quizCase)
    groups.set(quizCase.declaredType, group)
  }

  return groups
}

export function findQuizCase(
  cases: readonly QuizCaseDescriptor[],
  id: string | null | undefined,
): QuizCaseDescriptor | null {
  if (!id) return null
  return cases.find((quizCase) => quizCase.id === id) ?? null
}

export function filterQuizCases(
  cases: readonly QuizCaseDescriptor[],
  filters: QuizCaseFilters,
): readonly QuizCaseDescriptor[] {
  return cases.filter((quizCase) => (
    (!filters.declaredType || quizCase.declaredType === filters.declaredType)
    && (!filters.family || quizCase.family === filters.family)
    && (!filters.mode || quizCase.mode === filters.mode)
    && (!filters.variant || quizCase.variant === filters.variant)
    && (!filters.renderStatus || quizCase.renderStatus === filters.renderStatus)
    && (!filters.answerState || quizCase.answerState === filters.answerState)
    && (!filters.resultState || quizCase.resultState === filters.resultState)
    && (!filters.tag || quizCase.tags.includes(filters.tag))
  ))
}
